import { Users, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts'
import type { ClientRevenue, HoursSplit } from '@/hooks/useDashboardPremium'
import { cn } from '@/lib/utils'

interface DashboardDistributionSectionProps {
  clientRevenues: ClientRevenue[]
  hoursSplit: HoursSplit
}

const formatCurrency = (value: number) => 
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const formatHours = (hours: number) => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}h`
  return `${h}h ${m}min`
}

const COLORS = [
  'hsl(224, 71%, 40%)',   // Primary blue
  'hsl(142, 71%, 45%)',   // Success green
  'hsl(38, 92%, 50%)',    // Warning orange
  'hsl(280, 65%, 55%)',   // Purple
  'hsl(190, 70%, 45%)',   // Teal
  'hsl(350, 65%, 55%)',   // Rose
]

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-card border rounded-lg shadow-lg p-3 text-sm">
        <p className="font-medium">{data.clientName}</p>
        <p className="text-muted-foreground">{formatCurrency(data.revenue)}</p>
        <p className="text-xs text-muted-foreground">{data.percentage.toFixed(1)}% do total</p>
      </div>
    )
  }
  return null
}

export function DashboardDistributionSection({
  clientRevenues,
  hoursSplit
}: DashboardDistributionSectionProps) {
  const totalHours = hoursSplit.billable + hoursSplit.nonBillable
  const billablePercent = totalHours > 0 ? (hoursSplit.billable / totalHours) * 100 : 0
  const nonBillablePercent = totalHours > 0 ? (hoursSplit.nonBillable / totalHours) * 100 : 0

  const hoursData = [
    { 
      name: 'Faturável', 
      hours: hoursSplit.billable, 
      percent: billablePercent,
      fill: 'hsl(var(--success))'
    },
    { 
      name: 'Não Faturável', 
      hours: hoursSplit.nonBillable, 
      percent: nonBillablePercent,
      fill: 'hsl(var(--muted-foreground))'
    },
  ]

  // Show "Sem dados" if no client revenues
  const hasClientData = clientRevenues.length > 0

  return (
    <section className="grid gap-4 lg:grid-cols-2">
      {/* Revenue by Client Donut Chart */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Receita por Cliente
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasClientData ? (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={clientRevenues.slice(0, 6)}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="revenue"
                    nameKey="clientName"
                  >
                    {clientRevenues.slice(0, 6).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend 
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    formatter={(value, entry: any) => (
                      <span className="text-xs text-foreground">{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-[220px] flex items-center justify-center">
              <p className="text-sm text-muted-foreground">Sem dados de receita este mês</p>
            </div>
          )}
          
          {/* Client dependency warning */}
          {clientRevenues.some(c => c.percentage > 60) && (
            <div className="mt-3 p-2 bg-destructive/10 border border-destructive/20 rounded-lg">
              <p className="text-xs text-destructive font-medium">
                ⚠️ Alta concentração de receita em um cliente
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Billable vs Non-billable Hours */}
      <Card className="shadow-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Horas Trabalhadas
          </CardTitle>
        </CardHeader>
        <CardContent>
          {totalHours > 0 ? (
            <>
              {/* Stats at top */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 bg-success/10 rounded-xl border border-success/20">
                  <p className="text-xs text-muted-foreground mb-1">Faturáveis</p>
                  <p className="text-xl font-bold text-success">{formatHours(hoursSplit.billable)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(hoursSplit.billableValue)} gerados
                  </p>
                </div>
                <div className="p-3 bg-muted rounded-xl border">
                  <p className="text-xs text-muted-foreground mb-1">Não Faturáveis</p>
                  <p className="text-xl font-bold">{formatHours(hoursSplit.nonBillable)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {nonBillablePercent.toFixed(0)}% do total
                  </p>
                </div>
              </div>

              {/* Horizontal bar chart */}
              <div className="h-[100px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={hoursData} layout="vertical" margin={{ left: 0, right: 10 }}>
                    <XAxis type="number" hide />
                    <YAxis 
                      type="category" 
                      dataKey="name" 
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                      width={100}
                    />
                    <Tooltip 
                      formatter={(value: number) => [formatHours(value), 'Horas']}
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar 
                      dataKey="hours" 
                      radius={[0, 4, 4, 0]}
                      barSize={24}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="h-[220px] flex items-center justify-center">
              <p className="text-sm text-muted-foreground">Sem registros de tempo este mês</p>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
